// #3248 PR 2 (R1 F1 / R3 F1) — keep magic-link tokens out of the web app's
// Sentry.
//
// The emailed link is /action#t=<token>. Sentry.init (main.tsx) runs before
// React renders, so before ActionPage strips the fragment:
//   - browserTracingIntegration's pageload transaction records the full URL
//     (request.url, span data) including #t=<token>;
//   - the strip itself (history.replaceState) becomes a navigation breadcrumb
//     { from: '/action#t=<token>', to: '/action' }, and that breadcrumb rides on
//     every later error event from the tab (errors are not sampled).
// The fragment never reaches OUR servers, but Sentry is a server too. So every
// event, transaction and breadcrumb goes through this scrubber:
//   - a URL whose path is /action loses its fragment, whatever it holds (not
//     just `t=`: the page reads `%74=` as `t=` too);
//   - any JWT-shaped substring anywhere becomes "[Filtered]";
//   - a secret in a URL becomes "[Filtered]" (#3564 decision 2, AVR, PR #3666
//     comment 6327023198). The browser SDK does send page URLs carrying them:
//     the emailed /accept-invite, /client/accept-invite, /setup/accept,
//     /academic/accept, /client-onboarding, /onboard/set-password and
//     /reset-password links put ?token= in the address bar, /auth/callback and
//     /verify-email put ?code= there, and /register / /login carry them inside
//     ?redirect=. They reach request.url, the pageload / navigation
//     transaction, navigation breadcrumbs, and the fetch breadcrumbs and spans
//     of /partner/clients/invite/<token>/validate and /invitations/<token>/status.
//     maskUrlSecrets (@blueprint/shared) is the same helper the API uses.
// The first two are matched on the string as sent AND on its percent-decoded form
// (decoded until stable), so `%2E` dots, `%74=` or double encoding can't hide a
// token (Codex R2 F1). A string still decoding at the cap is filtered whole.
// Pure and synchronous. On an unexpected shape it drops the breadcrumb or keeps
// only a bare event — an unscrubbed event is never the fallback.
// apps/api/src/lib/sentryScrub.ts applies the same string rules on the API.

import type { BrowserOptions } from '@sentry/react';
import { maskQueryString, maskUrlSecrets } from '@blueprint/shared';

const FILTERED = '[Filtered]';
const JWT_LIKE = /eyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g;
// A URL (absolute, or a bare path at a word boundary) whose path is /action,
// with its optional query; group 1 keeps all of that, the `#…` after it goes.
const ACTION_FRAGMENT = /((?:^|[\s"'(<=,;]|[a-z][a-z0-9+.-]*:\/\/[^/\s"'#?]*)\/action\/?(?:\?[^#\s"'<>]*)?)#[^\s"'<>]*/gi;
const MAX_DECODE_ROUNDS = 5;

/** One round of percent-decoding. A malformed escape anywhere makes
 * decodeURIComponent throw, so fall back to decoding each well-formed %XX
 * on its own (as Latin-1: enough to expose an ASCII token). Never throws. */
function decodeOnce(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s.replace(/%([0-9a-f]{2})/gi, (_m, hex: string) => String.fromCharCode(parseInt(hex, 16)));
  }
}

/** Percent-decoded until stable; null if it is still changing after the cap. */
function fullyDecoded(s: string): string | null {
  let cur = s;
  for (let i = 0; i < MAX_DECODE_ROUNDS; i++) {
    const next = decodeOnce(cur);
    if (next === cur) return cur;
    cur = next;
  }
  return decodeOnce(cur) === cur ? cur : null;
}

function redact(s: string): string {
  return maskUrlSecrets(s.replace(ACTION_FRAGMENT, '$1').replace(JWT_LIKE, FILTERED));
}

/** Redacts the string as sent; if its decoded form carries something to
 * redact, returns that form redacted instead (the encoding is not kept). */
export function scrubString(value: string): string {
  const raw = redact(value);
  if (!value.includes('%')) return raw;
  const decoded = fullyDecoded(value);
  if (decoded === null) return FILTERED;
  const cleaned = redact(decoded);
  return cleaned === decoded ? raw : cleaned;
}

function scrubDeep(value: unknown, seen: WeakSet<object>): unknown {
  if (typeof value === 'string') return scrubString(value);
  if (value === null || typeof value !== 'object') return value;
  if (seen.has(value)) return value;
  seen.add(value);
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const next = scrubDeep(value[i], seen);
      if (next !== value[i]) value[i] = next;
    }
    return value;
  }
  for (const key of Object.keys(value)) {
    // Write back only what changed: an event can hold getter-only properties, and
    // assigning one throws, which would replace the whole event with the
    // fail-closed fallback (pageload transactions were lost that way).
    const cur = (value as Record<string, unknown>)[key];
    const next = scrubDeep(cur, seen);
    if (next !== cur) (value as Record<string, unknown>)[key] = next;
  }
  return value;
}

/** Events and transactions: scrubbed in place; on failure, a bare event with nothing that can carry a token. */
export function scrubSentryEvent<T extends object>(event: T): T {
  try {
    // A string query_string is masked by the walk like any string; this also
    // covers the [key, value][] and { key: value } shapes the type allows.
    const request = (event as { request?: { query_string?: unknown } }).request;
    if (request && request.query_string !== undefined) {
      const qs = maskQueryString(request.query_string);
      if (qs !== request.query_string) request.query_string = qs;
    }
    return scrubDeep(event, new WeakSet()) as T;
  } catch {
    const e = event as Record<string, unknown>;
    return { event_id: e?.event_id, level: e?.level, message: FILTERED } as unknown as T;
  }
}

/** Breadcrumbs: scrubbed in place; on failure, dropped. */
export function scrubBreadcrumb<T extends object>(crumb: T): T | null {
  try {
    return scrubDeep(crumb, new WeakSet()) as T;
  } catch {
    return null;
  }
}

/** The options main.tsx passes to Sentry.init (minus integrations, which need the SDK instance). */
export function webSentryOptions(env: { dsn?: string; mode: string; release?: string }): BrowserOptions {
  return {
    dsn: env.dsn,
    environment: env.mode,
    release: env.release,
    tracesSampleRate: env.mode === 'production' ? 0.1 : 1.0,
    enabled: !!env.dsn,
    beforeSend: (event) => scrubSentryEvent(event),
    beforeSendTransaction: (event) => scrubSentryEvent(event),
    beforeBreadcrumb: (crumb) => scrubBreadcrumb(crumb),
  };
}
